import {
  Box,
  FieldSelect,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  Form,
  getFormValues,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';
import { required } from 'redux-form-validators';
import { v4 as uuid } from 'uuid';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { availabilityOptions } from 'components/settings/components/sections/dixaMessenger/views/flow/components/nodeTypeAvailability/availabilityModal/availabilityOptions';
import {
  FlowMessengerNodeAvailability,
  FlowMessengerNodeAvailabilityQuery,
  FlowMessengerNodeAvailabilityStrategy,
} from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

interface AvailabilityFormValues {
  query?: FlowMessengerNodeAvailabilityStrategy;
}

interface OwnProps
  extends FlowManagerEditModalComponentT<FlowMessengerNodeAvailability> {
  options: FlowMessengerNodeAvailabilityQuery[];
}

interface StoreProps {
  formValues: AvailabilityFormValues;
  initialValues: {
    query: FlowMessengerNodeAvailabilityStrategy;
  };
}

type Props = OwnProps &
  StoreProps &
  InjectedFormProps<AvailabilityFormValues, OwnProps>;

const defaultAvailabilityStrategy: FlowMessengerNodeAvailabilityStrategy =
  'noAgentsAvailable';

const AvailabilityModalImpl = (props: Props): JSX.Element => {
  const {
    node,
    nodeLabel,
    triggerClose,
    handleSubmit,
    destroy,
    replaceNode,
    pristine,
    formValues,
  } = props;

  const closeModal = useCallback(() => {
    destroy();
    triggerClose();
  }, [destroy, triggerClose]);

  const notify = useNotifications();

  const handleSaveClick = useCallback(
    (values: AvailabilityFormValues) => {
      if (!values.query) {
        notify({ title: 'No strategy is selected' }, NotificationType.Error);
        return;
      }

      const availableNextNodeId = node.payload?.availableNextNodeId || uuid();

      const notAvailableNextNodeId =
        node.payload?.notAvailableNextNodeId || uuid();

      const nodeOptions: FlowMessengerNodeAvailability = {
        ...node,
        badges: [],
        icon: '',
        text: '',
        isAction: false,
        isEditable: true,
        isRemovable: true,
        nodeType: FlowNodeTypes.MESSENGER_AVAILABILITY,
        payload: {
          query: { type: values.query },
          availableNextNodeId,
          notAvailableNextNodeId,
        },
        overlay: [],
      };

      replaceNode(nodeOptions);
      triggerClose();
    },
    [node, replaceNode, triggerClose, notify],
  );

  const disabledSubmit =
    pristine && formValues.query !== defaultAvailabilityStrategy;

  return (
    <Box width="600px">
      <Form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalHead title={nodeLabel} onClose={closeModal}>
          Agent availability is used to check if any agents are available.
          Availability is decided on whether agents in the downstream Queues are
          either at Workload Capacity, have their status set to away, or are
          simply offline.
        </ModalHead>

        <ModalBody>
          <Field
            required
            name="query"
            label="Agent Availability"
            placeholder="Select an availability strategy"
            validate={[required()]}
            component={FieldSelect}
            items={availabilityOptions}
          />
        </ModalBody>

        <ModalFoot
          confirmOnClick={handleSubmit(handleSaveClick)}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: disabledSubmit }}
          cancelOnClick={closeModal}
        />
      </Form>
    </Box>
  );
};

const formName = 'messengerAvailabilityModalForm';

const mapStateToProps = (state: RootReducerStateT, ownProps: OwnProps) => {
  const formValues = (getFormValues(formName)(state) ||
    {}) as AvailabilityFormValues;
  return {
    initialValues: {
      query: ownProps.node.payload?.query.type ?? defaultAvailabilityStrategy,
    },
    formValues,
  };
};

export const AvailabilityModal = connect<
  StoreProps,
  Record<string, unknown>,
  OwnProps,
  RootReducerStateT
>(mapStateToProps)(
  reduxForm<AvailabilityFormValues, Props>({
    form: formName,
    enableReinitialize: true,
  })(AvailabilityModalImpl),
);
